import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Car } from 'lucide-react';
import { vehicleService } from '@/services/VehicleService';
import { VehicleInfo, VehicleInputProps, VehicleSuggestion } from '@/types';
import './VehicleInput.css';

const SAMPLE_VINS = [
  "5UXTR7C5XKLE88914",
  "5UXCR6C5XKLL40379",
  "5UXCR6C58KLL07185",
  "5UXCR6C58KLL11821",
  "5UXKU2C58K0Z63035",
  "5UXKU2C56K0Z62921"
];

export const VehicleInput: React.FC<VehicleInputProps> = ({ onVehicleSelect, className = '' }) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<VehicleSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decodedVehicle, setDecodedVehicle] = useState<VehicleInfo | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const isVIN = (value: string): boolean => {
    return /^[A-HJ-NPR-Z0-9]{17}$/.test(value);
  };

  const filterVINs = (searchString: string): VehicleSuggestion[] => {
    if (!searchString) return [];
    
    return SAMPLE_VINS
      .filter(vin => vin.toLowerCase().includes(searchString.toLowerCase()))
      .map(vin => ({
        value: vin,
        year: '',
        make: '',
        model: '',
        display: vin
      }));
  };

  const fetchSuggestions = useCallback(async (searchString: string) => {
    if (!searchString) {
      setSuggestions([]);
      return;
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Show VIN suggestions immediately
    const vinSuggestions = filterVINs(searchString);
    setSuggestions(vinSuggestions);

    // Only proceed with YMM search if input is long enough and not a VIN
    if (searchString.length < 3 || isVIN(searchString)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await vehicleService.getYMMSearch(searchString, controller.signal);
      const ymmSuggestions = Array.isArray(results) ? results.map(result => ({
        value: result,
        year: result.split(' ')[0],
        make: result.split(' ')[1],
        model: result.split(' ').slice(2).join(' '),
        display: result
      })) : [];

      // Combine and sort suggestions
      const combinedSuggestions = [...vinSuggestions, ...ymmSuggestions];
      setSuggestions(combinedSuggestions);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && ('name' in err) && ((err as { name?: string }).name === 'CanceledError' || (err as { name?: string }).name === 'AbortError')) {
        // Request was cancelled, do nothing
        return;
      }
      console.error('❌ Error fetching YMM suggestions:', err);
      setError('Failed to fetch suggestions');
      // Keep VIN suggestions even if YMM search fails
      setSuggestions(vinSuggestions);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleVehicleSelect = async (value: string) => {
    setIsLoading(true);
    setError(null);

    try {
      let vehicleInfo: VehicleInfo;

      if (isVIN(value)) {
        vehicleInfo = await vehicleService.getVehicleInfoByVIN(value);
      } else {
        vehicleInfo = await vehicleService.getYMMDecode(value);
      }

      setDecodedVehicle(vehicleInfo);
      onVehicleSelect(vehicleInfo);
      setInput(value);
      setSuggestions([]);
      setShowSuggestions(false);
    } catch (err) {
      console.error('❌ Error decoding vehicle:', err);
      setError('Failed to decode vehicle information');
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setShowSuggestions(true);
    setDecodedVehicle(null);
    fetchSuggestions(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input) {
      handleVehicleSelect(input);
    }
  };

  const handleSuggestionClick = (suggestion: VehicleSuggestion) => {
    handleVehicleSelect(suggestion.value);
  };

  // Effects
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    return (
    <div className={`vehicle-input-wrapper ${className}`}>
      <div className="input-wrapper">
        <Car className="input-icon" />
        <Input
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter VIN or Year/Make/Model"
          className="vehicle-input"
          ref={inputRef}
        />
        {isLoading && (
          <div className="loading-indicator">
            <div className="spinner" />
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-container" ref={suggestionsRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.display}
            </div>
          ))}
        </div>
      )}

      {decodedVehicle && (
        <div className="vehicle-data-display">
          <div className="vehicle-data-header">
            <Car className="vehicle-data-icon" />
            <span className="vehicle-data-title">Vehicle Information</span>
          </div>
          <div className="vehicle-data-content">
            <div className="vehicle-data-row">
              <span className="vehicle-data-label">Year:</span>
              <span className="vehicle-data-value">{decodedVehicle.year}</span>
            </div>
            <div className="vehicle-data-row">
              <span className="vehicle-data-label">Make:</span>
              <span className="vehicle-data-value">{decodedVehicle.make}</span>
            </div>
            <div className="vehicle-data-row">
              <span className="vehicle-data-label">Model:</span>
              <span className="vehicle-data-value">{decodedVehicle.model}</span>
            </div>
            {decodedVehicle.vin && (
              <div className="vehicle-data-row">
                <span className="vehicle-data-label">VIN:</span>
                <span className="vehicle-data-value">{decodedVehicle.vin}</span>
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
};

VehicleInput.displayName = 'VehicleInput';

export default VehicleInput;
